import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import './App.css';
import smartRedirectService from './services/smartRedirect';

const Header = () => {
  return (
    <div className="page-header">
      <a href="https://parkwithparallel.com" target="_blank" rel="noopener noreferrer">
        <img src="/assets/Logo_Parallel.svg" alt="Parallel" className="parallel-logo" />
      </a>
    </div>
  );
};

const DownloadButtons = ({ platform, showAll = false }) => {
  const buttons = [];
  
  if (showAll || platform === 'iOS' || platform === 'Desktop') {
    buttons.push(
      <a key="ios" href="https://apps.apple.com/ca/app/parallel-mobile/id6751863179" className="download-button">
        <img src="/assets/app_ios_download.svg" alt="Download for iOS" />
      </a>
    );
  }
  
  if (showAll || platform === 'Android' || platform === 'Desktop') {
    buttons.push(
      <a key="android" href="https://play.google.com/store/apps/details?id=com.parkwithparallel.app" className="download-button">
        <img src="/assets/app_android_download.svg" alt="Download for Android" />
      </a>
    );
  }
  
  // Always show web button
  buttons.push(
    <a key="web" href="https://pay.parkwithparallel.com" className="download-button">
      <img src="/assets/app_web.svg" alt="Use Web App" />
    </a>
  );
  
  return <div className="download-buttons">{buttons}</div>;
};

const HomePage = () => {
  const platform = smartRedirectService.detectPlatform();
  const showAll = platform === 'Desktop';
  
  return (
    <div className="page-container">
      <Header />
      <div className="page-box">
        <div className="lot-info">
          <img src="/assets/app_icon.png" alt="Try Parallel Today!" width="180" height="180"></img>
          <DownloadButtons platform={platform} showAll={showAll} />
        </div>
      </div>
    </div>
  );
};

const LotPage = () => {
  const { lotId } = useParams();
  const [redirectState, setRedirectState] = useState({
    isRedirecting: true,
    showWebInterface: false,
    redirectAttempted: false,
    platform: null,
    debugInfo: null
  });

  useEffect(() => {
    const initializeRedirect = async () => {
      if (!lotId) {
        console.error('No lot ID provided');
        setRedirectState(prev => ({
          ...prev,
          isRedirecting: false,
          showWebInterface: true,
          platform: smartRedirectService.detectPlatform()
        }));
        return;
      }

      const platform = smartRedirectService.detectPlatform();
      const debugInfo = smartRedirectService.getDebugInfo();
      
      console.log('Initializing smart redirect for lot:', lotId, debugInfo);

      // Small delay to ensure page is loaded before redirect
      setTimeout(async () => {
        await smartRedirectService.performSmartRedirect(lotId, {
          onWebFallback: () => {
            console.log('Using web interface for desktop');
            setRedirectState(prev => ({
              ...prev,
              isRedirecting: false,
              showWebInterface: true,
              redirectAttempted: true,
              platform,
              debugInfo
            }));
          },
          onAppSuccess: () => {
            console.log('Successfully opened mobile app');
            setRedirectState(prev => ({
              ...prev,
              isRedirecting: false,
              redirectAttempted: true,
              platform,
              debugInfo
            }));
          },
          onAppFallback: () => {
            console.log('Redirecting to app store');
            setRedirectState(prev => ({
              ...prev,
              isRedirecting: false,
              redirectAttempted: true,
              platform,
              debugInfo
            }));
          }
        });
      }, 100);
    };

    initializeRedirect();
  }, [lotId]);

  // Show loading state while attempting redirect
  if (redirectState.isRedirecting) {
    return (
      <div className="page-container">
        <Header />
        <div className="page-box">
          <div className="redirect-status">
            <div className="loading-spinner"></div>
            <div className="message">Redirecting to app...</div>
          </div>
        </div>
      </div>
    );
  }

  // Show web interface (desktop or mobile fallback)
  if (redirectState.showWebInterface) {
    const platform = redirectState.platform || smartRedirectService.detectPlatform();
    const showAll = platform === 'Desktop';
    
    return (
      <div className="page-container">
        <Header />
        <div className="page-box">
          <div className="lot-info">
            <div className="lot-id">Lot ID: {lotId}</div>
            <DownloadButtons platform={platform} showAll={showAll} />
          </div>
        </div>
      </div>
    );
  }

  // Redirect attempted but still showing page (mobile app should have opened)
  const platform = smartRedirectService.detectPlatform();
  const showAll = platform === 'Desktop';
  
  return (
    <div className="page-container">
      <Header />
      <div className="page-box">
        <div className="lot-info">
          <div className="lot-id">Lot ID: {lotId}</div>
          <DownloadButtons platform={platform} showAll={showAll} />
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/lotid/:lotId" element={<LotPage />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App; 